/**
 * Dostupnost tržeb.
 *
 * Ve fixture / dev prostředí je `price` = 0, takže všechny metriky odvozené
 * z tržeb (revenue, eCPM, win eCPM, fill rate, win rate) nemají výsledek.
 *
 * NULA NENÍ VÝSLEDEK. Vykreslit „0 Kč" nebo „0,00 %" by tvrdilo, že jsme
 * naměřili nulové tržby — což je něco jiného než „tržby v tomhle prostředí
 * neměříme". Statistická poctivost (00 §5.8) platí i tady: nedostupné číslo
 * se přizná, nepředstírá se.
 *
 * Backend to řeší dvěma způsoby a UI musí umět oba:
 *   1) `MetricValueOut.available === false` + `note_cs` — u KPI karet, kde
 *      backend sám ví, že metriku nespočítal. Tohle je preferovaná cesta.
 *   2) holé číslo (`PositionRowOut.revenue`, `ecpm`) — tam se dostupnost
 *      odvozuje z celkových tržeb za období, viz `hasRevenueData()`.
 */
import { REVENUE_DERIVED_METRICS, type MetricKey, type MetricValue } from '@/api/models';

export function isRevenueDerived(metric: MetricKey): boolean {
  return REVENUE_DERIVED_METRICS.includes(metric);
}

/**
 * Měří tohle prostředí vůbec tržby?
 *
 * Heuristika: když je součet tržeb za celé období přesně nula, nejde o „nic
 * jsme nevydělali", ale o prostředí bez dat o výnosech (fixture má `price` = 0).
 * Reálný provoz nikdy nemá přes celé období nulu na haléř přesně.
 */
export function hasRevenueData(totalRevenue: number | null | undefined): boolean {
  return typeof totalRevenue === 'number' && Number.isFinite(totalRevenue) && totalRevenue !== 0;
}

/**
 * Hodnota k zobrazení, nebo `null` když ji nemáme.
 * Když tržby v prostředí nejsou, vrací null i pro nulu z backendu — právě proto,
 * že fixture posílá nuly.
 */
export function revenueValue(
  value: number | null | undefined,
  revenueAvailable: boolean,
): number | null {
  if (!revenueAvailable) return null;
  if (value === null || value === undefined || !Number.isFinite(value)) return null;
  return value;
}

/** Hodnota z `MetricValueOut` — backend sám říká, jestli ji spočítal. */
export function metricValue(metric: MetricValue | undefined): number | null {
  if (!metric || !metric.available) return null;
  return metric.value;
}

/** KPI podle klíče z pole `MetricValueOut[]`, které vrací záložky detailu. */
export function findMetric(
  metrics: readonly MetricValue[] | undefined,
  key: MetricKey,
): MetricValue | undefined {
  return metrics?.find((m) => m.key === key);
}

/**
 * Vysvětlení, proč metrika chybí. Text jde do prázdného stavu, aby uživatel
 * nehádal, jestli je to chyba, nebo vlastnost prostředí.
 */
export function revenueUnavailableReason(context: 'fixture' | 'no_data' = 'fixture'): string {
  if (context === 'fixture') {
    return 'Fixture data neobsahují výnosy (price = 0), takže tržby, eCPM, fill rate a win rate nejde spočítat. Proti produkčním datům se doplní samy.';
  }
  return 'Za zvolené období nejsou ve zdroji žádné údaje o tržbách.';
}

/** Metriky, které v daném prostředí nemá smysl nabízet v přepínači. */
export function availableMetrics(
  metrics: readonly MetricKey[],
  revenueAvailable: boolean,
): MetricKey[] {
  if (revenueAvailable) return [...metrics];
  return metrics.filter((m) => !isRevenueDerived(m));
}
